import type { WebSocketClient } from "@/api/web-socket-client"

import { logger } from "@/lib/logger"

const registry = new Map<string, WebSocketClient>()
let lastSessionId: string | null = null
let lastClient: WebSocketClient | undefined
const shouldLogRegistryEvents =
  import.meta.env.DEV || import.meta.env.VITE_DEBUG_TOOL_APPROVALS === "true"

function logRegistryEvent(message: string, context: Record<string, unknown>) {
  if (!shouldLogRegistryEvents) {
    return
  }
  logger.debug(message, context)
}

export function registerSessionWebSocket(sessionId: string, client: WebSocketClient) {
  if (!sessionId) return
  registry.set(sessionId, client)
  lastSessionId = sessionId
  lastClient = client
  logRegistryEvent("Registered session WebSocket", {
    sessionId,
    totalSessions: registry.size,
  })
}

export function unregisterSessionWebSocket(sessionId: string, client?: WebSocketClient) {
  if (!sessionId) return
  const current = registry.get(sessionId)
  if (!current) return
  if (client && current !== client) return
  registry.delete(sessionId)
  if (lastSessionId === sessionId) {
    lastSessionId = null
    lastClient = undefined
  }
  logRegistryEvent("Unregistered session WebSocket", {
    sessionId,
    totalSessions: registry.size,
  })
}

export function getSessionWebSocket(sessionId: string | null): WebSocketClient | undefined {
  if (!sessionId) return undefined
  return registry.get(sessionId) ?? undefined
}

export function getLastRegisteredWebSocket():
  | { sessionId: string | null; client: WebSocketClient | undefined }
  | undefined {
  if (!lastClient) {
    return undefined
  }
  logRegistryEvent("Accessing last registered WebSocket", {
    sessionId: lastSessionId,
  })
  return { sessionId: lastSessionId, client: lastClient }
}

export function dumpWebSocketRegistry(reason?: string) {
  logRegistryEvent("WebSocket registry snapshot", {
    reason,
    sessionIds: Array.from(registry.keys()),
    lastSessionId,
  })
}
